import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';

import {
  DATE_MASK_TEMPLATE,
  DATETIME_MASK_TEMPLATE,
  DIGIT_PLACEHOLDER_CHARS,
  DIGIT_REGEX,
  MASK_SECTIONS_BY_TEMPLATE,
} from './DateFieldCDS.constants';

const YEAR_PAD_LENGTH = 4;
const TWO_DIGIT_PAD_LENGTH = 2;
const DECIMAL_RADIX = 10;
const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';

const isDigitPlaceholder = ch => DIGIT_PLACEHOLDER_CHARS.has(ch);

const isDigitChar = ch => DIGIT_REGEX.test(ch);

const isDigitSlot = (template, position) =>
  position >= 0 && position < template.length && isDigitPlaceholder(template[position]);

const findNextDigitSlot = (template, fromPosition) =>
  Array.from(template).findIndex((ch, idx) => idx >= fromPosition && isDigitPlaceholder(ch));

const findPrevDigitSlot = (template, beforePosition) =>
  Array.from(template).findLastIndex((ch, idx) => idx < beforePosition && isDigitPlaceholder(ch));

const findFirstUnfilledSlot = (mask, template) =>
  Array.from(template).findIndex((ch, idx) => isDigitPlaceholder(ch) && mask[idx] === ch);

const buildEmptyMask = (template = DATE_MASK_TEMPLATE) => template;

const replaceMaskCharAt = (mask, position, char) =>
  `${mask.slice(0, position)}${char}${mask.slice(position + 1)}`;

const restoreMaskCharAt = (mask, position, template) =>
  replaceMaskCharAt(mask, position, template[position]);

const isMaskFullyFilled = (mask, template = DATE_MASK_TEMPLATE) =>
  Array.from(template).every(
    (templateChar, idx) => !isDigitPlaceholder(templateChar) || isDigitChar(mask[idx]),
  );

export const isMaskFullyEmpty = (mask, template = DATE_MASK_TEMPLATE) =>
  Array.from(template).every(
    (templateChar, idx) => !isDigitPlaceholder(templateChar) || mask[idx] === templateChar,
  );

export const resolveMaskTemplate = (format, withTime) => {
  if (!isNil(format) && Object.prototype.hasOwnProperty.call(MASK_SECTIONS_BY_TEMPLATE, format)) {
    return format;
  }

  return withTime ? DATETIME_MASK_TEMPLATE : DATE_MASK_TEMPLATE;
};

export const formatDateToMask = (value, template = DATE_MASK_TEMPLATE) => {
  if (isNil(value)) {
    return buildEmptyMask(template);
  }

  const parsed = dayjs(value);

  if (!parsed.isValid()) {
    return buildEmptyMask(template);
  }

  return parsed.format(template);
};

// A year-only template ('YYYY') has no month/day sections, so it resolves to January 1st.
const buildIsoFromSections = sectionValues => {
  const { month = 1, day = 1, year, hour = 0, minute = 0 } = sectionValues;

  const datePart = `${String(year).padStart(YEAR_PAD_LENGTH, '0')}-${String(month).padStart(
    TWO_DIGIT_PAD_LENGTH,
    '0',
  )}-${String(day).padStart(TWO_DIGIT_PAD_LENGTH, '0')}`;

  const timePart = `${String(hour).padStart(TWO_DIGIT_PAD_LENGTH, '0')}:${String(minute).padStart(
    TWO_DIGIT_PAD_LENGTH,
    '0',
  )}:00`;

  return `${datePart}T${timePart}`;
};

const candidateMatchesSections = (candidate, sectionValues) => {
  const { month = 1, day = 1, year, hour = 0, minute = 0 } = sectionValues;

  return (
    candidate.month() + 1 === month &&
    candidate.date() === day &&
    candidate.year() === year &&
    candidate.hour() === hour &&
    candidate.minute() === minute
  );
};

export const parseMaskToDate = (mask, template = DATE_MASK_TEMPLATE) => {
  if (!isMaskFullyFilled(mask, template)) {
    return null;
  }

  const sections = MASK_SECTIONS_BY_TEMPLATE[template];
  const sectionValues = {};

  const allInRange = sections.every(section => {
    const value = parseInt(mask.substring(section.start, section.end), DECIMAL_RADIX);

    if (value < section.min || value > section.max) {
      return false;
    }

    sectionValues[section.key] = value;

    return true;
  });

  if (!allInRange) {
    return null;
  }

  const candidate = dayjs(buildIsoFromSections(sectionValues));

  if (!candidate.isValid()) {
    return null;
  }

  return candidateMatchesSections(candidate, sectionValues) ? candidate : null;
};

const areDatesEqualWithTemplate = (a, b, template) => {
  if (isNull(a) && isNull(b)) {
    return true;
  }

  if (isNull(a) || isNull(b)) {
    return false;
  }

  const dayjsA = dayjs(a);
  const dayjsB = dayjs(b);

  if (!dayjsA.isValid() && !dayjsB.isValid()) {
    return true;
  }

  if (!dayjsA.isValid() || !dayjsB.isValid()) {
    return false;
  }

  return dayjsA.format(template) === dayjsB.format(template);
};

export const areValuesEqual = (a, b, template = DATE_MASK_TEMPLATE) =>
  areDatesEqualWithTemplate(a, b, template);

const clampDate = (date, minDate, maxDate) => {
  if (isNull(date)) {
    return null;
  }

  if (maxDate && dayjs(maxDate).isBefore(date)) {
    return dayjs(maxDate);
  }

  if (minDate && dayjs(minDate).isAfter(date)) {
    return dayjs(minDate);
  }

  return date;
};

export const computeEmitDate = (nextValue, minDate, maxDate, originalValue) => {
  const clamped = clampDate(nextValue, minDate, maxDate);

  if (clamped && originalValue && originalValue.$offset === undefined) {
    delete clamped.$offset;
  }

  return clamped;
};

const insertDigitAtCursor = ({ mask, cursorPosition, digit, template }) => {
  const primarySlot = isDigitSlot(template, cursorPosition)
    ? cursorPosition
    : findNextDigitSlot(template, cursorPosition);

  const targetSlot = primarySlot === -1 ? findFirstUnfilledSlot(mask, template) : primarySlot;

  if (targetSlot === -1) {
    return { mask, nextCursorPosition: cursorPosition };
  }

  const nextMask = replaceMaskCharAt(mask, targetSlot, digit);
  const followingDigit = findNextDigitSlot(template, targetSlot + 1);
  const nextCursorPosition = followingDigit === -1 ? targetSlot + 1 : followingDigit;

  return { mask: nextMask, nextCursorPosition };
};

const deleteBeforeCursor = ({ mask, cursorPosition, template }) => {
  const targetSlot = findPrevDigitSlot(template, cursorPosition);

  if (targetSlot === -1) {
    return { mask, nextCursorPosition: cursorPosition };
  }

  const nextMask = restoreMaskCharAt(mask, targetSlot, template);

  return { mask: nextMask, nextCursorPosition: targetSlot };
};

const deleteAtCursor = ({ mask, cursorPosition, template }) => {
  const targetSlot = isDigitSlot(template, cursorPosition)
    ? cursorPosition
    : findNextDigitSlot(template, cursorPosition);

  if (targetSlot === -1) {
    return { mask, nextCursorPosition: cursorPosition };
  }

  const nextMask = restoreMaskCharAt(mask, targetSlot, template);

  return { mask: nextMask, nextCursorPosition: targetSlot };
};

const clearRange = ({ mask, selectionStart, selectionEnd, template }) => {
  const nextMask = Array.from(mask)
    .map((ch, idx) => {
      if (idx >= selectionStart && idx < selectionEnd && isDigitSlot(template, idx)) {
        return template[idx];
      }

      return ch;
    })
    .join('');

  return { mask: nextMask, nextCursorPosition: selectionStart };
};

const applyDigitsAtCursor = ({ mask, cursorPosition, digits, template }) =>
  Array.from(digits).reduce(
    (acc, digit) =>
      insertDigitAtCursor({
        mask: acc.mask,
        cursorPosition: acc.nextCursorPosition,
        digit,
        template,
      }),
    { mask, nextCursorPosition: cursorPosition },
  );

const readSelection = input => {
  const selectionStart = input.selectionStart ?? 0;
  const selectionEnd = input.selectionEnd ?? selectionStart;

  return {
    selectionStart,
    selectionEnd,
    hasSelection: selectionEnd > selectionStart,
  };
};

const getBaseMaskAfterSelectionClear = ({
  mask,
  selectionStart,
  selectionEnd,
  hasSelection,
  template,
}) => {
  if (!hasSelection) {
    return mask;
  }

  return clearRange({ mask, selectionStart, selectionEnd, template }).mask;
};

export const handleMaskKeyDown = ({ event, mask, template }) => {
  const { selectionStart, selectionEnd, hasSelection } = readSelection(event.currentTarget);

  if (event.key === KEY_BACKSPACE) {
    event.preventDefault();

    if (hasSelection) {
      return clearRange({ mask, selectionStart, selectionEnd, template });
    }

    return deleteBeforeCursor({ mask, cursorPosition: selectionStart, template });
  }

  if (event.key === KEY_DELETE) {
    event.preventDefault();

    if (hasSelection) {
      return clearRange({ mask, selectionStart, selectionEnd, template });
    }

    return deleteAtCursor({ mask, cursorPosition: selectionStart, template });
  }

  if (event.key.length === 1 && isDigitChar(event.key)) {
    event.preventDefault();

    const baseMask = getBaseMaskAfterSelectionClear({
      mask,
      selectionStart,
      selectionEnd,
      hasSelection,
      template,
    });

    return insertDigitAtCursor({
      mask: baseMask,
      cursorPosition: selectionStart,
      digit: event.key,
      template,
    });
  }

  return null;
};

export const handleMaskPaste = ({ event, mask, template }) => {
  event.preventDefault();

  const pasted = event.clipboardData?.getData?.('text') ?? '';
  const digits = pasted.replace(/\D/g, '');

  if (!digits) {
    return null;
  }

  const { selectionStart, selectionEnd, hasSelection } = readSelection(event.currentTarget);
  const baseMask = getBaseMaskAfterSelectionClear({
    mask,
    selectionStart,
    selectionEnd,
    hasSelection,
    template,
  });

  return applyDigitsAtCursor({
    mask: baseMask,
    cursorPosition: selectionStart,
    digits,
    template,
  });
};

export const focusEmptyMaskStart = (inputRef, template) => {
  if (!inputRef.current) {
    return;
  }

  const firstDigitSlot = findNextDigitSlot(template, 0);

  if (firstDigitSlot === -1) {
    return;
  }

  const input = inputRef.current;

  window.requestAnimationFrame(() => {
    input.setSelectionRange(firstDigitSlot, firstDigitSlot);
  });
};

const ensureDayjs = value => {
  if (isNil(value)) {
    return null;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed : null;
};

export const toDayjsOrNull = value => ensureDayjs(value);

export const mergeCalendarSelectionWithMask = ({ selectedDate, mask, template }) => {
  if (isNull(selectedDate)) {
    return null;
  }

  const parsedFromMask = parseMaskToDate(mask, template);

  if (isNull(parsedFromMask)) {
    return dayjs(selectedDate);
  }

  return dayjs(selectedDate)
    .hour(parsedFromMask.hour())
    .minute(parsedFromMask.minute())
    .second(0)
    .millisecond(0);
};

export const mergeTimeSelectionWithMask = ({ selectedTime, mask, template }) => {
  if (isNull(selectedTime)) {
    return null;
  }

  const time = dayjs(selectedTime);

  if (!time.isValid()) {
    return null;
  }

  const parsedFromMask = parseMaskToDate(mask, template);
  const baseDate = isNull(parsedFromMask) ? time : parsedFromMask;

  return baseDate.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
};
